/**
 * UserConfigModal - Modal for managing user configuration flags
 */

import { useState, useEffect } from 'react'
import { User } from '../api'
import { useUserConfig, useUpdateUserConfig } from '../hooks/useUserConfig'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Settings, Info } from 'lucide-react'
import { toast } from 'sonner'

interface UserConfigModalProps {
  user: User | null
  open: boolean
  onClose: () => void
}

/**
 * Config structure definition - organized by categories
 * Makes it super easy to add new config flags - just add to the appropriate category!
 */
const CONFIG_STRUCTURE = {
  features: {
    label: 'Features',
    description: 'Feature flags and capabilities',
    icon: '⚡',
    flags: [
      {
        key: 'studioAccess',
        label: 'Studio Access',
        description: 'Allow user to access the AI Studio workspace',
        icon: '🎨',
      },
      // ADD NEW FEATURE FLAGS HERE:
      // {
      //   key: 'betaFeatures',
      //   label: 'Beta Features',
      //   description: 'Enable experimental features',
      //   icon: '🧪',
      // },
    ],
  },
  // ADD NEW CATEGORIES HERE:
  // preferences: {
  //   label: 'Preferences',
  //   description: 'User preferences and settings',
  //   icon: '⚙️',
  //   flags: [],
  // },
  // limits: {
  //   label: 'Limits',
  //   description: 'Resource quotas and limits',
  //   icon: '📊',
  //   flags: [],
  // },
} as const

export function UserConfigModal({ user, open, onClose }: UserConfigModalProps) {
  const [localFlags, setLocalFlags] = useState<Record<string, boolean>>({})
  const [hasChanges, setHasChanges] = useState(false)

  const {
    data: configData,
    isLoading: isLoadingConfig,
    error: configError,
  } = useUserConfig(user?._id || '')

  const { mutate: updateConfig, isPending: isUpdating } = useUpdateUserConfig()

  // Initialize local state when config data loads
  useEffect(() => {
    if (configData) {
      // Flatten nested config for easier state management
      const flattened: Record<string, any> = {}
      Object.entries(CONFIG_STRUCTURE).forEach(([category, categoryData]) => {
        categoryData.flags.forEach((flag) => {
          const path = `${category}.${flag.key}`
          flattened[path] = configData[category]?.[flag.key] ?? false
        })
      })
      setLocalFlags(flattened)
      setHasChanges(false)
    }
  }, [configData])

  // Handle flag toggle
  const handleFlagChange = (path: string, value: boolean) => {
    setLocalFlags((prev) => ({
      ...prev,
      [path]: value,
    }))
    setHasChanges(true)
  }

  // Save all changes
  const handleSave = () => {
    if (!user) return

    // Build updates object with only changed flags (using dot notation)
    const updates: Record<string, boolean> = {}
    Object.entries(CONFIG_STRUCTURE).forEach(([category, categoryData]) => {
      categoryData.flags.forEach((flag) => {
        const path = `${category}.${flag.key}`
        const oldValue = configData?.[category]?.[flag.key] ?? false
        const newValue = localFlags[path]

        if (newValue !== oldValue) {
          updates[path] = newValue
        }
      })
    })

    if (Object.keys(updates).length === 0) {
      toast.info('No changes to save')
      return
    }

    // Update all changed flags in one request
    updateConfig(
      {
        userId: user._id,
        updates: updates,
      },
      {
        onSuccess: () => {
          toast.success('Config updated successfully')
          setHasChanges(false)
          onClose()
        },
        onError: (error) => {
          console.error('Failed to update config:', error)
          toast.error('Failed to update config')
        },
      }
    )
  }

  // Handle close (with unsaved changes warning)
  const handleClose = () => {
    if (hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirm) return
    }
    setHasChanges(false)
    onClose()
  }

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            User Configuration
          </DialogTitle>
          <DialogDescription>
            Manage feature flags and settings for {user.name || user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLoadingConfig && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {configError && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load config. Please try again.
              </AlertDescription>
            </Alert>
          )}

          {!isLoadingConfig && !configError && (
            <>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Changes will be reflected in the main application immediately after saving.
                </AlertDescription>
              </Alert>

              <div className="space-y-6">
                {Object.entries(CONFIG_STRUCTURE).map(([category, categoryData]) => (
                  <div key={category}>
                    <div className="mb-3">
                      <h3 className="flex items-center gap-2 text-sm font-semibold">
                        <span>{categoryData.icon}</span>
                        {categoryData.label}
                      </h3>
                      <p className="text-xs text-muted-foreground">{categoryData.description}</p>
                    </div>
                    <div className="space-y-3">
                      {categoryData.flags.map((flag) => {
                        const path = `${category}.${flag.key}`
                        return (
                          <div
                            key={path}
                            className="flex items-start justify-between space-x-4 rounded-lg border p-4"
                          >
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{flag.icon}</span>
                                <Label htmlFor={path} className="text-sm font-medium">
                                  {flag.label}
                                </Label>
                              </div>
                              <p className="text-xs text-muted-foreground">{flag.description}</p>
                            </div>
                            <Switch
                              id={path}
                              checked={localFlags[path] || false}
                              onCheckedChange={(value) => handleFlagChange(path, value)}
                              disabled={isUpdating}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isUpdating || isLoadingConfig}
          >
            {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
